import { supabase } from '../supabase';
import { Database } from '../../types/database';

type Invoice = Database['public']['Tables']['invoices']['Row'];
type InvoiceInsert = Database['public']['Tables']['invoices']['Insert'];
type InvoiceUpdate = Database['public']['Tables']['invoices']['Update'];
type InvoiceItem = Database['public']['Tables']['invoice_items']['Row'];
type InvoiceItemInsert = Database['public']['Tables']['invoice_items']['Insert'];

export const invoiceService = {
  /**
   * Get all invoices with client and items
   */
  getInvoices: async () => {
    const { data, error } = await supabase
      .from('invoices')
      .select(`
        *,
        client:clients(*),
        invoice_items(*)
      `)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  /**
   * Get invoice by ID with full details
   */
  getInvoiceById: async (id: string) => {
    const { data, error } = await supabase
      .from('invoices')
      .select(`
        *,
        client:clients(*),
        invoice_items(*),
        payments(*)
      `)
      .eq('id', id)
      .single();
    return { data, error };
  },

  /**
   * Get invoices by client ID
   */
  getInvoicesByClient: async (clientId: string) => {
    const { data, error } = await supabase
      .from('invoices')
      .select(`
        *,
        client:clients(*),
        invoice_items(*)
      `)
      .eq('client_id', clientId)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  /**
   * Get invoices by status
   */
  getInvoicesByStatus: async (status: 'draft' | 'sent' | 'paid' | 'overdue') => {
    const { data, error } = await supabase
      .from('invoices')
      .select(`
        *,
        client:clients(*),
        invoice_items(*)
      `)
      .eq('status', status)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  /**
   * Create a new invoice
   */
  createInvoice: async (invoice: Omit<InvoiceInsert, 'id' | 'created_at' | 'updated_at' | 'invoice_number'>) => {
    const { data, error } = await supabase
      .from('invoices')
      .insert(invoice)
      .select()
      .single();
    return { data, error };
  },

  /**
   * Update an existing invoice
   */
  updateInvoice: async (id: string, updates: InvoiceUpdate) => {
    const { data, error } = await supabase
      .from('invoices')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },

  /**
   * Delete an invoice
   */
  deleteInvoice: async (id: string) => {
    const { data, error } = await supabase
      .from('invoices')
      .delete()
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },

  /**
   * Create invoice items
   */
  createInvoiceItems: async (items: InvoiceItemInsert[]) => {
    const { data, error } = await supabase
      .from('invoice_items')
      .insert(items)
      .select();
    return { data, error };
  },

  /**
   * Update invoice items
   */
  updateInvoiceItems: async (invoiceId: string, items: InvoiceItemInsert[]) => {
    // Delete existing items
    await supabase
      .from('invoice_items')
      .delete()
      .eq('invoice_id', invoiceId);

    // Insert new items
    const { data, error } = await supabase
      .from('invoice_items')
      .insert(items)
      .select();
    return { data, error };
  },

  /**
   * Get overdue invoices
   */
  getOverdueInvoices: async () => {
    const { data, error } = await supabase
      .from('invoices')
      .select(`
        *,
        client:clients(*),
        invoice_items(*)
      `)
      .eq('status', 'overdue')
      .order('due_date', { ascending: true });
    return { data, error };
  },

  /**
   * Mark invoice as paid
   */
  markAsPaid: async (id: string) => {
    const { data, error } = await supabase
      .from('invoices')
      .update({ status: 'paid' })
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },

  /**
   * Send invoice (mark as sent)
   */
  sendInvoice: async (id: string) => {
    const { data, error } = await supabase
      .from('invoices')
      .update({ status: 'sent' })
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  }
};